import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    Inject,
    OnDestroy,
    signal,
    ViewChild,
} from '@angular/core';
import {
    CdkDragDrop,
    DragDropModule,
    moveItemInArray,
    transferArrayItem,
} from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { DOCUMENT, NgClass, NgStyle } from '@angular/common';
import { TaskCardComponent } from '@lib-ui/task-card/task-card.component';
import { WorkerCardComponent } from '../../components/worker-card/worker-card.component';
import { MapComponent } from '../../components/map/map.component';
import { FilterPanelComponent } from '../../components/filter-panel/filter-panel.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { OrdersService } from '@lib-services/orders.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInput, MatInputModule } from '@angular/material/input';
import { Params } from '@angular/router';
import { LoaderComponent } from '@lib-ui/loader/loader.component';
import { MessageService } from '@lib-services/message.service';
import { MapService } from '../../components/map/map.service';
import { PersonService } from '@lib-services/person.service';
import { IShowHideExecutor } from '@lib-typing/person.interface';
import { IOrderDataUpdate } from '@lib-typing/order.interface';
import { MatDialog } from '@angular/material/dialog';
import { MultiEditOrderFormComponent } from '@lib-ui/forms/multi-edit-order-form/multi-edit-order-form.component';
import { AssignOrdersFormComponent } from '@lib-ui/forms/assign-orders-form/assign-orders-form.component';
import { concatMap, EMPTY, filter, interval, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { AssignOrdersService } from '@lib-services/assigns-orders.service';
import { OrderStatusEnum, PersonStatusEnum } from '@libs-enums/status.enum';
import { EnumService } from '@lib-services/enum.service';
import { UploadCsvComponent } from '../../shared/upload-csv/upload-csv.component';
import { EnumTranslateService } from '@lib-services/enum-translate.service';
import { Order } from '@libs-classes/order.class';
import { Role } from '@libs-enums/role.enum';
import { TasksCountComponent } from '../../shared/tasks-count/tasks-count.component';
import { Person } from '@libs-classes/person.class';
import { ReportService } from '@lib-services/report.service';
import { ISelectPoint } from '@lib-typing/map-point.interface';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { MainPageService } from '@lib-services/main-page.service';
import { TIME_ZONE_EN, TimeZoneEnum } from '@libs-enums/time-zone.enum';
import { upDateTasksInHour } from '../../../../../../libs/utils/update-tasks-In-hour';

@Component({
    selector: 'app-main-page',
    standalone: true,
    imports: [
        FilterPanelComponent,
        MatCheckbox,
        MatTooltip,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatLabel,
        MapComponent,
        DragDropModule,
        MatIconModule,
        NgClass,
        NgStyle,
        TaskCardComponent,
        WorkerCardComponent,
        MatButton,
        ReactiveFormsModule,
        MatFormField,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatInput,
        LoaderComponent,
        FormsModule,
        MatIconButton,
        TasksCountComponent,
    ],
    templateUrl: './main-page.component.html',
    styleUrl: './main-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainPageComponent implements AfterViewInit, OnDestroy {
    workers: Person[] = [];
    workerOrdersMap: Map<number, Order[]> = new Map();
    newOrders: Order[] = [];
    selectedOrders: Set<Order> = new Set();
    selectedOrdersIds: number[] = [];
    loading = signal<boolean>(false);
    filterText = '';
    filteredNewOrders: Order[] = [];
    sortDirection = 'ASC';
    queryParams: Params = {};
    timelineSteps: string[] = [];
    workerCardSettings: IShowHideExecutor = {
        time: new Date(),
        show: true,
    };
    showAllExecutors = signal<IShowHideExecutor>(this.workerCardSettings);
    hiddenExecutors: number[] = [];
    protected readonly OrderStatusEnum = OrderStatusEnum;
    timelineInterval = 1; // стандартный интервал шкалы
    startTime = 0; // время начала работы JCall
    endTime = 24; // время конца отображения заявок JCall
    showAll = true;
    tempWorkerOrders: Order[] = []; // временно хранятся задачи, которые перетягивают на работника
    // эти 2 переменные для того, чтобы показать пользователю сообщение, что задача этому работнику назначится не может
    assignedOrders: Order[] = [];
    showFilters = signal<boolean>(true);
    highlightedOrderId = signal<number|null>(null);
    highlightedWorkerId = signal<number|null>(null);
    highlightedMapPoint = signal<number|null>(null);
    showActiveTasks = false;
    activeWorkersCount = 0;
    cardWidth = 30; // ширина карточки на шкале
    startHourScreen = 8;
    tasksInHour = 5;
    refreshInterval = 5; // в минутах
    highlightedTaskId: number | null = null;
    position = ''; // справа или слева поместить заявки
    isDrag = false; // контролируем перетягивается ли сейчас заявка или нет
    canDrag = false; // контролируем можем ли мы переместить в это место заявку

    mapHeight = signal<number>(300); // Начальная высота карты
    private isResizing = false;
    private startY = 0;
    private startHeight = 0;

    private destroy$ = new Subject<void>(); // Управление жизненным циклом таймера
    @ViewChild('orderCard', { static: false }) orderCard!: ElementRef;
    @ViewChild(MapComponent) mapComponent!: MapComponent;
    @ViewChild('tableContainer', { static: true }) tableContainer!: ElementRef;
    @ViewChild('boardSection', { static: true }) boardSection!: ElementRef;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ordersService: OrdersService,
        protected timeZoneService: TimeZoneService,
        private messageService: MessageService,
        private mapService: MapService,
        private personService: PersonService,
        private assignOrdersService: AssignOrdersService,
        private reportService: ReportService,
        private dialog: MatDialog,
        protected enumTranslateService: EnumTranslateService,
        protected mainPageService: MainPageService,
        protected enumService: EnumService,
    ) {
        this.generateTimelineSteps();
        this.startPeriodicSearch();

        effect(() => {
            if (this.timeZoneService.currentTimeZone()) {
                this.search(this.queryParams);
                this.setHorizontalScroll();
            }
        }, { allowSignalWrites: true });
    }

    ngAfterViewInit(): void {
        this.setHorizontalScroll();
        this.boardSection.nativeElement.style.setProperty('--map-height', `${this.mapHeight()}px`);
    }

    startResize(event: MouseEvent): void {
        this.isResizing = true;
        this.startY = event.clientY;
        this.startHeight = this.mapHeight();

        this.document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.document.addEventListener('mouseup', this.stopResize.bind(this));
        event.preventDefault();
    }

    handleMouseMove(event: MouseEvent): void {
        if (!this.isResizing) {return;}

        const newHeight = this.mapComponent.calculateNewHeight(
            this.startY,
            this.startHeight,
            event.clientY
        );

        this.mapHeight.set(newHeight);
        // Обновляем CSS переменную
        this.boardSection.nativeElement.style.setProperty('--map-height', `${newHeight}px`);
        this.mapComponent.updateMapSize();
    }

    stopResize(): void {
        this.isResizing = false;
        this.document.removeEventListener('mousemove', this.handleMouseMove);
        this.document.removeEventListener('mouseup', this.stopResize);

        this.mapComponent.updateMapSize();
    }

    get hourWidth(): number {
        return this.cardWidth * this.tasksInHour;
    }

    setHorizontalScroll(): void {
        // cardWidth - ширина карточки
        // widthOneHour - ширина одного часа. this.widthOneHour / this.cardWidth карточек вслезают в час
        // startHourScreen - сдвиг на какое время скролла
        const scrollOffset = this.cardWidth * (this.hourWidth / this.cardWidth) * this.startHourScreen;
        this.tableContainer.nativeElement.scrollTo({ left: scrollOffset, behavior: 'smooth' });
    }

    onMouseEnter(orderId: number): void {
        this.highlightedMapPoint.set(orderId);
        this.highlightedTaskId = orderId;
    }

    onMouseLeave(): void {
        this.highlightedMapPoint.set(null);
        this.highlightedTaskId = null;
    }

    // при задатии на заявке отключаем ховер у работников и устанавливаем флаг, что сейчас чтото перетягивается
    onMouseDown(): void {
        this.isDrag = true;
        const orderCards = document.querySelectorAll('.order-card');
        orderCards.forEach(card => card.classList.add('no-hover'));
    }

    onMouseUp(): void {
        this.isDrag = false;
        const orderCards = document.querySelectorAll('.order-card');
        orderCards.forEach(card => card.classList.remove('no-hover'));
    }

    // смотрим над какой заявкой курсор во время перетягивания
    isHovered(taskId: number): boolean {
        return this.highlightedTaskId === taskId;
    }

    // выбираем справа или слева от заявки поместить перетягиваемые заявки
    setPosition(position: string): void {
        this.position = position;
    }

    // устанавливаем можем ли отпустить и перетянуть заявку в это место
    setCanDrag(drag: boolean): void {
        this.canDrag = drag;
    }

    // выделение одной или нескольких задач
    toggleOrderSelection(order: Order): void {
        if (this.selectedOrders.has(order)) {
            this.selectedOrders.delete(order);
            order.data.selected = false;
        } else {
            this.selectedOrders.add(order);
            order.data.selected = true;
        }
    }

    toggleAllWorkersSelection(checked: boolean): void {
        this.workers.forEach(worker => this.selectAllWorkerOrders(worker.data.id, checked));
    }

    get areAllOrdersSelected(): boolean {
        return this.workers.every(worker => this.areAllWorkerOrdersSelected(worker.data.id));
    }

    // Проверить, выбраны ли некоторые задачи
    get someOrdersSelected(): boolean {
        return this.workers.some(worker => this.someWorkerOrdersSelected(worker.data.id));
    }

    // проверка есть ли выделенные задачи
    isOrderSelected(order: Order): boolean {
        return this.selectedOrders.has(order);
    }

    toggleShowHideAll(): void {
        this.showAll = !this.showAll;
        this.showHideAll(this.showAll);
    }

    showHideAll(show: boolean): void {
        if (show) {
            this.hiddenExecutors = [];
        } else {
            this.hiddenExecutors = this.workers.map(worker => worker.data.id);
        }
        this.updateWorkerVisibility();
    }

    // скрыть \ показать настройки работника
    updateWorkerVisibility(): void {
        this.workerCardSettings = {
            time: new Date(),
            show: this.showAll,
        };
        this.showAllExecutors.set(this.workerCardSettings);
    }

    drop(event: CdkDragDrop<Order[]>): void {
        const worker = this.workers.find(worker => worker.data.id === Number(event.container.id.slice(7)));
        if(worker?.data.status === PersonStatusEnum.inactive) {
            this.messageService.warningMessage('Задача не может быть назначена данному работнику');
            return;
        }

        // фильтрация только перетянутых задач
        let dataToUpdate: Order[];
        let draggedId: string | number | null = event.container.id;
        const previousContainer: string | number | null = event.previousContainer.id;

        ///для протягивания заявки если между работниками если не выбрано куда вставляем в конец
        if(draggedId !== previousContainer && !this.position && !this.selectedOrders.size) {
            this.position = 'right';
        }

        //если в это место не можем перетянуть или пытаемся в то же место - останавливаем перетягивание
        if((!this.canDrag || !this.position) &&  !this.selectedOrders.size) {
            this.onMouseUp();
            return;
        }

        this.loading.set(true);

        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            const ordersToMove = this.selectedOrders.size > 0
                ? Array.from(this.selectedOrders)
                : [event.item.data as Order];

            ordersToMove.forEach(order => {
                const index = event.previousContainer.data.findIndex(item => item.data.id === order.data.id);
                if (index !== -1) {
                    transferArrayItem(
                        event.previousContainer.data,
                        event.container.data,
                        index,
                        event.currentIndex
                    );
                }
            });

            this.selectedOrders.clear();
        }

        if (draggedId.includes('worker')) {
            draggedId = Number(draggedId.replace('worker-', ''));
            dataToUpdate = event.container.data.filter(order => order.data.executor?.id !== draggedId).map(item => {
                item.data.queueNumber = null;
                return item;
            });
        } else {
            dataToUpdate = event.container.data.filter(order => order.data.executor?.id);
        }

        const prevWorkerId = Number(event.previousContainer.id.slice(7));
        const prevWorkerTasks = this.workerOrdersMap.get(prevWorkerId);
        let prevWorkerOrders: IOrderDataUpdate[] = [];
        if(prevWorkerTasks && prevWorkerId && prevWorkerId !== draggedId) {
            prevWorkerOrders = prevWorkerTasks.map((order, i) => order.refreshQueue(order, i));
        }
        // Определяем, куда была перемещена заявка (слева или справа)
        const hoveredTaskId = this.highlightedTaskId;
        if (hoveredTaskId && draggedId !== 'newOrdersList') {
            const hoveredTaskIndex = event.container.data.findIndex(task => task.data.id === hoveredTaskId);
            if (hoveredTaskIndex !== -1) {
                const movedOrder = event.item.data; // Заявка, которую перетягивали
                let updateOrders: Order[];

                if(dataToUpdate.length) {
                    updateOrders = dataToUpdate; // если заявки перетягивались с другого работника или нераспределенных
                } else if (this.selectedOrders.size) {
                    updateOrders = [...this.selectedOrders]; // если заявки из разных мест или несколько внутри одного работника
                } else {
                    updateOrders = [movedOrder]; // одна заявка внутри одного работника
                }

                // если не выбрана позиция - останавливаем перетягивание

                const isLeft = this.position === 'left';
                this.updateOrders([
                    ...this.assignOrdersService.changeQueueManualDrop(
                        updateOrders,
                        event.container.data,
                        hoveredTaskId,
                        isLeft,
                        Number(draggedId)
                    ),
                    ...prevWorkerOrders
                ]
                );
            }
            this.onMouseUp();
            return;

        }

        // для заявок, которые руками перемещают в нераспределенные
        if (draggedId === 'newOrdersList') {
            this.updateOrders([...dataToUpdate.map(item => item.dataForCancelDistributedOrders()), ...prevWorkerOrders]);
        } else {
            this.tempWorkerOrders = event.container.data;
            if (!worker) {
                this.loading.set(false);
                this.onMouseUp();
                return;
            }
            // переназначение очереди
            const concatenatedOrders = this.assignOrdersService.setQueueAfterMultiEdit(event.container.data.sort((a,b) =>
                new Date(a.data.dateFrom).getHours() - new Date(b.data.dateFrom).getHours()));

            this.updateOrders([
                ...concatenatedOrders.map(item => item.dataForMultipleUpdateExecutorIdAndStatus(worker.data.id)), ...prevWorkerOrders
            ]);
        }
        this.onMouseUp();
        this.selectedOrders.clear();
    }

    updateOrders(data: IOrderDataUpdate[]): void {
        this.ordersService.updateOrders(data).pipe(
            switchMap(() => this.getAllOrders(this.queryParams))
        ).subscribe({
            next: (allOrders) => {
                this.processOrders(allOrders);
                this.messageService.successMessage('Задачи успешно переназначены');
                this.selectedOrders.clear();
            },
            error: () => this.messageService.errorMessage('При обновлении задач произошла ошибка')
        }).add(() => this.loading.set(false));
    }

    // выделение всех не назначенных задач
    selectAllOrders(select: boolean): void {
        this.newOrders.forEach(order => {
            order.data.selected = select;
            if (select) {
                this.selectedOrders.add(order);
            } else {
                this.selectedOrders.delete(order);
            }
        });
        this.newOrders = [...this.newOrders];
    }

    // выделение всех задач работника
    selectAllWorkerOrders(workerId: number, isChecked: boolean): void {
        const workerTasks = this.getWorkerTasks(workerId);
        workerTasks.forEach(order => {
            order.data.selected = isChecked;
            if (isChecked) {
                this.selectedOrders.add(order);
            } else {
                this.selectedOrders.delete(order);
            }
        });
    }

    getWorkerTasks(workerId: number): Order[] {
        return this.workerOrdersMap.get(workerId) || [];
    }

    // проверка на выделение всех задач работника
    areAllWorkerOrdersSelected(workerId: number): boolean {
        const workerTasks = this.getWorkerTasks(workerId);
        return workerTasks.every(order => this.selectedOrders.has(order));
    }

    // Проверить, выбраны ли некоторые задачи у работника
    someWorkerOrdersSelected(workerId: number): boolean {
        const workerTasks = this.getWorkerTasks(workerId);
        return workerTasks.some(order => this.selectedOrders.has(order));
    }

    // добавление задачи работнику
    getWorkerListIds(): string[] {
        return this.workers.map(worker => 'worker-' + worker.data.id);
    }

    // добавление в не назначенные задачи
    getConnectedListIds(currentMasterId: number): string[] {
        return ['newOrdersList', ...this.getWorkerListIds().filter(id => id !== `worker-${currentMasterId}`)];
    }

    refreshData(): void {
        this.mapComponent.updateMap = true;
        this.search(this.queryParams);
    }

    getAllOrders(query: Params, incognito?: boolean): Observable<Order[]> {
        if (!query['filter.group']) {
            return EMPTY;
        }

        this.queryParams = query;
        if (incognito) {
            // Сохраняем идентификаторы выделенных задач
            this.selectedOrdersIds = Array.from(this.selectedOrders).map(order => order.data.id);
            this.selectedOrders.clear();
        } else {
            this.loading.set(true);
        }

        return this.loadWorkers().pipe(
            switchMap(workers => {
                this.workers = workers.sort((current, next) => {
                    // Если статусы разные, active идет первым
                    if (current.data.status !== next.data.status) {
                        return current.data.status === PersonStatusEnum.active ? -1 : 1;
                    }
                    // Если статусы одинаковые, порядок не меняется
                    return 0;
                });
                return this.ordersService.getAllOrders(query);
            })
        );
    }
    // поиск по фильтрам
    search(query: Params, incognito?: boolean): void {
        this.getAllOrders(query, incognito).subscribe({
            next: (allOrders) => this.processOrders(allOrders, incognito),
            error: (error) => this.messageService.errorMessage(error),
        }).add(() => this.loading.set(false));
    }

    private processOrders(allOrders: Order[], incognito?: boolean): void {
        allOrders.forEach(order => {
            if (this.selectedOrdersIds.includes(order.data.id)) {
                this.toggleOrderSelection(order); // Выделяем задачу
            }
        });
        this.assignedOrders = [];
        // Создаем новую карту для связи работников и заявок
        this.workerOrdersMap.clear();
        this.workers.forEach(worker => {
            const workerOrders = allOrders.filter(order => order.data.executor?.id === worker.data.id);
            this.assignedOrders.push(...workerOrders);
            this.workerOrdersMap.set(worker.data.id, this.sortOrdersByQueueNumber(workerOrders));
            allOrders = allOrders.filter(order => order.data.executor?.id !== worker.data.id);
        });
        this.activeWorkersCount = this.workers.filter(worker => worker.data.status === PersonStatusEnum.active).length;
        this.newOrders = allOrders;

        this.tasksInHour = upDateTasksInHour(this.workerOrdersMap);
        this.setHorizontalScroll();

        // Определяем направление сортировки
        if(!incognito) {
            this.sortDirection = 'ASC';
        }
        this.sortOrdersByDateFrom(this.sortDirection, false); // Автоматическая сортировка
        this.applyFilter();

        this.updateMap();
    }

    // переход на страницу проссмотра\редактирования задачи
    showOrderInfo(id: number): void {
        const link = `/order-editor/${id}`;
        this.document.defaultView?.open(link, '_blank');
    }

    // фильтрация не назначенных задач
    applyFilter(): void {
        this.filteredNewOrders = this.newOrders.filter(order =>
            order.data.additionalInfo?.toLowerCase().includes(this.filterText.toLowerCase()) ||
            order.data.location?.address.toLowerCase().includes(this.filterText.toLowerCase())
        );
    }

    // сортировка заявок
    sortOrdersByDateFrom(sortDirection: string, isManualSort = false): void {
        if (isManualSort) {
            // Если сортировка вызвана вручную, обновляем направление сортировки
            this.sortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
        }

        this.newOrders = [...this.newOrders].sort((a, b) => {
            const dateA = new Date(a.data.dateFrom).getTime();
            const dateB = new Date(b.data.dateFrom).getTime();

            if (dateA === dateB) {
                return this.sortDirection === 'ASC' ? a.data.id - b.data.id : b.data.id - a.data.id;
            }

            return this.sortDirection === 'ASC' ? dateA - dateB : dateB - dateA;
        });

        this.applyFilter(); // Обновляем отфильтрованный список
    }

    private sortOrdersByQueueNumber(orders: Order[]): Order[] {
        return orders.sort((a, b) => (a.data.queueNumber || 0) - (b.data.queueNumber || 0));
    }

    // получение всех данных
    loadWorkers(): Observable<Person[]> {
        const workersQuery = {
            'filter.group.id': this.queryParams['filter.group'],
            'filter.role': Role.executor,
            'filter.dateFrom': this.queryParams['filter.dateFrom'],
            sortBy:'id:DESC'
        };
        return this.personService.getAllPersons(workersQuery);
    }

    assignOrders(): void {
        this.dialog.open(AssignOrdersFormComponent, {
            width: '50%',
            height: '80%',
            maxHeight: '500px',
            disableClose: true,
            data: {
                workers: this.workers.filter(worker => worker.data.status === PersonStatusEnum.active),
                newOrders: this.newOrders,
                workerOrdersMap: this.workerOrdersMap
            }
        }).afterClosed().pipe(filter(result => result)).subscribe(() => {
            this.search(this.queryParams);
            this.selectedOrders.clear();
        });
    }

    cancelDistributedOrders(): void {
        this.messageService.confirmMessage({
            text: 'Вы действительно хотите отменить распределение?'
        }).pipe(
            filter(result => result),
            concatMap(() =>  {
                this.loading.set(true);
                return this.assignOrdersService.cancelDistributedOrders(Array.from(this.workerOrdersMap.values()).flat());
            }),
            concatMap(() => this.getAllOrders(this.queryParams))
        ).subscribe((allOrders) => {
            this.processOrders(allOrders);
        }).add(() => this.loading.set(false));
    }

    changeOrdersStatusToReady(): void {
        this.loading.set(true);
        this.assignOrdersService.changeOrdersStatusToReady(Array.from(this.workerOrdersMap.values()).flat())
            .pipe(
                switchMap(() => this.getAllOrders(this.queryParams))
            )
            .subscribe({
                next: (allOrders) => {
                    this.processOrders(allOrders);
                },
                error: (error) => this.messageService.errorMessage(error.error),
            }).add(() => this.loading.set(false));
    }

    private generateTimelineSteps(): void {
        const steps = [];
        for (let hour = this.startTime; hour < this.endTime; hour += this.timelineInterval) {
            steps.push(this.formatTime(hour));
        }

        this.timelineSteps = steps;
    }

    getTasksForSlot(tasks: Order[] | undefined, slotIndex: number): Order[] {
        if (!tasks || tasks.length === 0) {
            return [];
        }

        const slotStartHour = this.startTime + slotIndex * this.timelineInterval;
        const slotEndHour = slotStartHour + this.timelineInterval;

        const filterTimeTasks = tasks.filter(task => {
            const taskHour = new Date(task.data.dateFrom).getHours();
            return taskHour >= slotStartHour && taskHour < slotEndHour;
        });

        return this.sortOrdersByQueueNumber(filterTimeTasks);
    }

    // форматирование времени в часы:минуты
    private formatTime(hour: number): string {
        const formattedHour = hour < 10 ? `0${hour}` : hour.toString();
        return `${formattedHour}`;
    }

    // передача массива выделенных заявок в форму редактирования
    editSelectedOrders(): void {
        this.dialog.open(MultiEditOrderFormComponent, {
            width: '50%',
            disableClose: true,
            data: {
                selectedOrders: Array.from(this.selectedOrders), // Преобразуем Set в массив
                workerOrdersMap: this.workerOrdersMap,
                currentDay: this.queryParams['filter.dateFrom']
            }
        }).afterClosed().subscribe(() => {
            this.selectedOrders.clear();
            this.search(this.queryParams);
        });
    }

    // скрытие\показание задач работников на панели и на карте
    showHideExecutors(event: IShowHideExecutor): void {
        if (!event.id) { return; }

        // добавление или удаление id работника из массива hiddenExecutors в зависимости от флага show
        if (event.show) {
            this.hiddenExecutors.push(event.id);
        } else {
            this.hiddenExecutors = this.hiddenExecutors.filter(id => id !== event.id);
        }
        this.updateMap();
    }

    updateMap(): void {
        // фильтрация только задач работников, которые не скрыты
        const onlyShowOrders = this.workers
            .filter(worker => this.hiddenExecutors.includes(worker.data.id))
            .map(worker => this.getWorkerTasks(worker.data.id).map(task => task.data)); // Собираем только их задачи

        const workers = this.workers.map(worker => worker.data);
        this.mapService.addExecutorsPoints(workers);
        // перерисовка не скрытых задач на карте
        this.mapService.addMapPoints(onlyShowOrders.flat());
        this.addActiveOrdersOnMap();
    }

    addActiveOrdersOnMap(): void {
        this.mapService.addActiveOrders(this.showActiveTasks ? this.filteredNewOrders.map(order => order.data) : []);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete(); // Завершаем поток, чтобы избежать утечек памяти
    }

    // Обновляем данные периодически с интервалом
    startPeriodicSearch(): void {
        interval(60 * 1000 * this.refreshInterval) // Интервал в миллисекундах
            .pipe(takeUntil(this.destroy$)) // Завершить поток при уничтожении компонента
            .subscribe(() => {
                if (!this.queryParams['filter.group']) {return;}
                this.search(this.queryParams, true);
            });
    }

    // загрузка CSV файлов
    uploadCSV():void {
        this.dialog.open(UploadCsvComponent, {
            width: '80%',
            height: 'auto',
            maxWidth: '400px',
            hasBackdrop: true,
            disableClose: true,
        }).afterClosed()
            .pipe(
                switchMap(() => {
                    this.loading.set(true);
                    return this.getAllOrders(this.queryParams);
                })
            )
            .subscribe({
                next: (allOrders) => {
                    this.processOrders(allOrders);
                    this.selectedOrders.clear();
                }
            }).add(() => this.loading.set(false));
    }

    saveReport(): void {
        this.reportService.saveReport({
            ...this.queryParams,
            timeZone: TIME_ZONE_EN[this.timeZoneService.currentTimeZone() as TimeZoneEnum]
        }).subscribe({
            next: (blob: Blob) => {
                // Создаем ссылку для скачивания файла
                const url = URL.createObjectURL(blob);
                const a = this.document.createElement('a');
                a.href = url;
                a.download = `executor_load_report_${new Date().getTime()}.csv`;
                this.document.body.appendChild(a);
                a.click();

                // Очистка
                URL.revokeObjectURL(url);
                a.remove();
            },
            error: () => this.messageService.errorMessage('Ошибка выгрузки отчета')
        });
    }

    deleteOrders(): void {
        const data = Array.from(this.selectedOrders).map(el => el.data.id);
        const hasExecutor = Array.from(this.selectedOrders).some(el => el.data.executor);
        const text = hasExecutor ?
            'Заявки, которые вы хотите удалить, распределены на работников. Вы уверены, что хотите удалить все заявки?' :
            'Вы уверены, что хотите удалить все заявки?';

        this.messageService.confirmMessage({
            title: 'Удаление заявок',
            text: text,
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter (confirm => confirm),
            concatMap (() => {
                this.loading.set(true);
                return this.ordersService.deleteOrders(data);
            }),
            concatMap (() => {
                return this.getAllOrders(this.queryParams);
            })
        ).subscribe({
            next: (allOrders) => {
                this.messageService.successMessage('Заявки успешно удалены');
                this.processOrders(allOrders);
                this.selectedOrders.clear();
            },
            error: () => this.messageService.errorMessage('Произошла ошибка при удалении'),
            complete: () => this.loading.set(false)
        });
    }

    toggleFilters(): void {
        this.showFilters.set(!this.showFilters());
    }

    markAsTouched(partElementId: string, id: number | null): void {
        const signal = partElementId === 'order' ? this.highlightedOrderId : this.highlightedWorkerId;

        if (id) {
            signal.set(id);
            this.scrollTo(partElementId, id);
            return;
        }

        const orderElement = document.getElementById(`${partElementId}-${signal()}`);
        if (orderElement) {
            orderElement.classList.remove('highlighted');
        }

        signal.set(null);
    }
    scrollTo(partElementId: string, id: number): void {
        const orderElement = document.getElementById(`${partElementId}-${id}`);
        if (orderElement) {
            orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    hideRow(workerId: number): boolean {
        return this.hiddenExecutors.some(id => workerId === id);
    }

    multiHighlightedMapPoints(data: ISelectPoint): void {
        data.orders.forEach((orderId: number) => {
            const orderElement = document.getElementById(`order-${orderId}`);
            if(!orderElement) {
                return;
            }

            data.cancel ?
                orderElement.classList.remove('multi-highlighted')
                : orderElement.classList.add('multi-highlighted');
        });
    }

    get selectedOrdersForMap(): number[] {
        return Array.from(this.selectedOrders).map(order => order.data.id);
    }
}

